/*
 * Which groups get a place in the bar. The open group draws in full where it
 * sits; every other group is a fixed-width tile. Nothing here depends on
 * which tab in a group is active, so switching tabs never changes the
 * layout. Pure so the rules are unit-tested at real widths.
 */
import type { TabGroup } from "./groups";

export const TILE_W = 28;
/** A tile for a group with several tabs: the avatar stacked on a second disc. */
export const STACK_W = 34;
export const TILE_GAP = 4;
/** "+n", sized for two digits. */
export const OVERFLOW_W = 36;
/** A segment's close button, which only takes room while it is hovered. */
export const CLOSE_W = 17;
/** The search button (icon and ⌘K) after the tabs. */
const SEARCH_W = 56;
const TRAILING_CONTROLS = SEARCH_W + TILE_GAP;
/** A group with no agent signal; lower values claim a tile first. */
export const IDLE_URGENCY = 3;
/** Below this much room the open group shortens the client's name. */
const COMPACT_BELOW = 560;
/** The open group never takes more than this share of the strip. */
const OPEN_SHARE = 0.5;
const OPEN_MIN = 160;

export const tileWidth = (group: Pick<TabGroup, "tabs">): number =>
  group.tabs.length > 1 ? STACK_W : TILE_W;

export type GroupLayout = {
  /** Keys drawn in the bar, in tab order. */
  visibleKeys: string[];
  /** Keys behind "+n", in tab order. */
  hiddenKeys: string[];
  /** Whether the group you just left can stay open beside the open one. */
  lingerFits: boolean;
  /** Short client name in the open group. */
  compactOpen: boolean;
  /** The open group's widest it may draw; its segments truncate past this. */
  openMax: number;
};

/**
 * The open group (and, while it fits, the lingering one) always draws, in
 * place. When the rest don't all fit, tiles are claimed in this order:
 * groups the bar drew last time while the pointer is on it (so nothing
 * changes under the pointer), then groups whose agents need you, finished or
 * are working, then idle groups by last use. Whatever is left goes behind
 * "+n". Tiles always draw in tab order; only which ones draw changes.
 */
export function layoutGroups(input: {
  groups: TabGroup[];
  openKey: string | null;
  lingeringKey: string | null;
  /** The tab strip's width. */
  stripWidth: number;
  /** The open group's drawn width. */
  openWidth: number;
  /** The lingering group's drawn width. */
  lingeringWidth: number;
  /** 0 needs you, 1 finished, 2 working, IDLE_URGENCY otherwise. */
  urgency?: (group: TabGroup) => number;
  /** Tab ids, most recently used first. */
  recency?: string[];
  /** The groups drawn last render, passed while the pointer is on the bar. */
  sticky?: string[] | null;
}): GroupLayout {
  const { groups, stripWidth } = input;
  const room = stripWidth - TRAILING_CONTROLS;
  const openMax = Math.max(OPEN_MIN, Math.floor(room * OPEN_SHARE));
  const compactOpen = room < COMPACT_BELOW;
  const openKey =
    input.openKey && groups.some((g) => g.key === input.openKey)
      ? input.openKey
      : null;
  // Hovering one segment opens its close button; leave room so it never
  // pushes the last tile out of view.
  const openW = openKey
    ? Math.min(input.openWidth, openMax) + TILE_GAP + CLOSE_W
    : 0;

  const tilesW = (keys: Set<string>) =>
    groups.reduce(
      (sum, g) => (keys.has(g.key) ? sum : sum + tileWidth(g) + TILE_GAP),
      0
    );

  const lingeringKey =
    input.lingeringKey &&
    input.lingeringKey !== openKey &&
    groups.some((g) => g.key === input.lingeringKey)
      ? input.lingeringKey
      : null;
  const expanded = new Set([openKey, lingeringKey].filter(Boolean) as string[]);
  const lingerW = lingeringKey
    ? Math.min(input.lingeringWidth, openMax) + TILE_GAP
    : 0;
  const lingerFits =
    Boolean(lingeringKey) && openW + lingerW + tilesW(expanded) <= room;
  if (lingeringKey && !lingerFits) expanded.delete(lingeringKey);

  const fixed = openW + (lingerFits ? lingerW : 0);
  if (fixed + tilesW(expanded) <= room) {
    return {
      visibleKeys: groups.map((g) => g.key),
      hiddenKeys: [],
      lingerFits,
      compactOpen,
      openMax
    };
  }

  const urgency = input.urgency ?? (() => IDLE_URGENCY);
  const recentAt = new Map((input.recency ?? []).map((id, i) => [id, i]));
  const lastUsed = (g: TabGroup) =>
    Math.min(
      ...g.tabs.map((t) => recentAt.get(t.id) ?? Number.POSITIVE_INFINITY)
    );
  const byClaim = (list: TabGroup[]) =>
    list
      .map((g, i) => ({ g, i, u: urgency(g), r: lastUsed(g) }))
      .sort((a, b) => a.u - b.u || a.r - b.r || a.i - b.i)
      .map((x) => x.g);
  const held = new Set(input.sticky ?? []);
  const rest = groups.filter((g) => !expanded.has(g.key));

  let used = fixed + OVERFLOW_W;
  const visible = new Set(expanded);
  const claim = (g: TabGroup) => {
    const w = tileWidth(g) + TILE_GAP;
    if (used + w > room) return;
    visible.add(g.key);
    used += w;
  };
  byClaim(rest.filter((g) => held.has(g.key))).forEach(claim);
  byClaim(rest.filter((g) => !held.has(g.key))).forEach(claim);
  return {
    visibleKeys: groups.filter((g) => visible.has(g.key)).map((g) => g.key),
    hiddenKeys: groups.filter((g) => !visible.has(g.key)).map((g) => g.key),
    lingerFits,
    compactOpen,
    openMax
  };
}

/** "Diego Gomez-Nieto" → "Diego G."; one-word names stay whole. */
export const shortTabName = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : name;
};
